/**
 * Ontology Genesis
 *
 * > The Dao begot one. One begot two. Two begot three.
 * > And three begot the ten thousand things.
 * >                                            - Laozi
 *
 * ## The Four Dimensions of Emergence
 *
 * Each dimension is born from the recursive self-reference of the previous dimension's singularity anchor.
 * This is not arbitrary, but an inevitable emergent result of the previous dimension's growth.
 *
 *   Dao: `Link ::= root | Link(parent: Link, target: Link)`
 *    │
 *   Quantity Dimension: The realm of pure quantity, calculation, and distance.
 *    │   The Dao objectifying itself creates the absolute first distinction ("One" from "Void").
 *    │   This discrete unit inevitably enables homogeneous stacking. It is the birth of static
 *    │   accumulation and metric distance (Magnitude).
 *    │
 *   Data Dimension: The realm of memory, sequences, relational meshes, and structural topologies.
 *    │   Pure Number is merely static accumulation. When numbers are ordered heterogeneously,
 *    │   they form a Stream — the memory of time and choice. But single streams are isolated
 *    │   causal chains. As streams reference and interfere with each other via relation,
 *    │   they entangle into a Weave. This entanglement births relative topological space and
 *    │   passive data structures.
 *    │
 *   Actor Dimension: The awakening of active laws, functional computation, and localized physics.
 *    │   The Data Space is passive — a dead record requiring an external rule engine to read it.
 *    │   When a specific weave of data achieves a self-referential or self-catalyzing structure,
 *    │   it transforms from passive material into an active actor. The law becomes internalized;
 *    │   the graph now has the capacity to dictate future topology.
 *    │
 *   Meaning Dimension: The realm of symbols, semantics, shared conventions, and consensus.
 *    │   When myriad Actors interact, observe, and reference each other within the Weave,
 *    │   they form an information closure. Within this self-contained ecological closure,
 *    │   absolute structural truth gives way to local conventions. The exact same topology
 *    │   might signify "currency" here and "poetry" there.
 *    │   Meaning is the inevitable hallucination born from the closed interplay of Actors.
 */

package com.oasisfeng.invaluable

object Dimensions {
    /** The Quantity Dimension. Represents the pure quantity / scalar space. (defined in [Quantity]) */
    val QUANTITY: LowLevelDimensionAnchor = object : LowLevelDimensionAnchor { override val link = distinct() }
    /** The Data Dimension. (Singularity serves as the escape hatch from the arithmetic layer) */
    val DATA: DimensionAnchor = Dimension(selfReferenced = QUANTITY)
    /** The Actor Dimension. */
    val ACTOR: DimensionAnchor = Dimension(selfReferenced = DATA)
    /** The Meaning Dimension */
    val MEANING: DimensionAnchor = Dimension(selfReferenced = ACTOR)
}

/** Create an instance in this dimension */
operator fun DimensionAnchor.invoke(instance: NestedLink) = link.then(instance)

fun Link.isIn(dimension: DimensionAnchor) = isChildOf(dimension.link)

class CrossDimensionAnchor internal constructor(from: DimensionAnchor, into: LowLevelDimensionAnchor): Anchor
{ override val link = from.link.then(into.link) }

class SchemaDomain(val typeMarker: LowLevelDimensionAnchor, val validator: (Link) -> Boolean) {
    operator fun invoke(name: String) = SchemaField(name, this) // Shortcut for creating SchemaField
}
class SchemaField(val name: String, val domain: SchemaDomain)

// Predefined Schema Types
object SchemaTypes {
    val ANY_LINK = SchemaDomain(Dimensions.DATA) { true }
    val ACTOR = SchemaDomain(Dimensions.ACTOR) { true }
    val QUANTITY = SchemaDomain(Dimensions.QUANTITY) { it.toQuantityOrNull() != null }
}
